import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UpdateInvoiceParsingInputPayload,
  type UpdateInvoiceParsingInputResponse,
} from './types';
import {
  zodInvoiceParsingInputFileDataSchema,
  zodUpdateInvoiceParsingInputEndpointSchema,
} from './validations';

/** Statuses that get their own message below, so they are logged as warnings instead of reported as errors. */
const EXPECTED_ERROR_STATUSES = new Set([400, 404, 422]);

/**
 * Updates the parsing input for a reprocessing invoice with the given process ID.
 * This function sends a new file (JSON or XML) to be used as the parsing input for the invoice.
 * @param payload - The payload containing the process ID and parsing input.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/update_invoice_process_parsing_input_invoice_process__process_id__parsing_input_put
 */
export const updateInvoiceParsingInput = async ({
  processId,
  parsingInput,
}: UpdateInvoiceParsingInputPayload) => {
  try {
    // Validate input data
    const validatedFileData =
      zodInvoiceParsingInputFileDataSchema.parse(parsingInput);

    const formData = new FormData();

    const fileTypeMime =
      validatedFileData.fileType === 'application/json'
        ? 'application/json'
        : 'application/xml';

    const file = new File(
      [validatedFileData.fileContent],
      validatedFileData.fileName,
      { type: fileTypeMime },
    );

    formData.append('parsingInput', file);

    const response = await vestigasApi
      .put(ENDPOINT.INVOICE_REPROCESSING.UPDATE_PARSING_INPUT(processId), {
        body: formData,
        validate: { pathSegments: zodUpdateInvoiceParsingInputEndpointSchema },
      })
      .json<UpdateInvoiceParsingInputResponse>();

    return response;
  } catch (error) {
    const status =
      error instanceof HTTPError ? error.response.status : undefined;
    const message = 'Error updating invoice parsing input';

    if (status !== undefined && EXPECTED_ERROR_STATUSES.has(status)) {
      log.warn({ message, data: error });
    } else {
      log.error({ message, error });
    }

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(($) => $.updateInvoiceParsingInput.error400, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.updateInvoiceParsingInput.error422, {
            ns: 'data',
          });
          break;
        }

        case 404: {
          errorMessage = i18n.t(($) => $.updateInvoiceParsingInput.error404, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.updateInvoiceParsingInput.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
